import type { IntlShape } from 'react-intl';

import { AuthenticationProvider } from '~/enums/AuthenticationProvider';
import { ClientSyncType } from '~/enums/ClientSyncType';
import { ClientType } from '~/enums/ClientType';
import { CloudType } from '~/enums/CloudType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { UserManagement } from '~/enums/UserManagement';
import { LdGatedFeatureOption } from '~/features/admin/types';
import { FlagType } from '~/flags/flags';

export const DEFAULT_CLAUSES_NUMBER = 5;
export const DEFAULT_XRAY_MODELS_NUMBER = 5;
const UPAS_CACHE_DELAY_HOURS = 3;
export const UPAS_CACHE_DELAY_MS = UPAS_CACHE_DELAY_HOURS * 60 * 60 * 1000;

export const INITIAL_PAGE_SIZE = 10;

// Unified Fields has its own create-client toggle (UnifiedFieldsSetting), not
// an entry here: Select All on Enable Features was turning it on for clients
// that were not meant to be Unified Fields-native.
export const LD_GATED_FEATURE_OPTIONS: LdGatedFeatureOption[] = [
  {
    flag: FlagType.EnableSchemaPerClient,
    optionValue: 'ENGINEERING_PROVISION_SCHEMA',
    configKey: 'provisionSchema',
    label: {
      id: 'AdminConstants.schemaPerClientLabel',
      defaultMessage: '(ENGINEERING USE ONLY) Schema Per Client',
      description:
        'Feature flag option for per-tenant Postgres schema provisioning',
    },
    createOnly: true,
  },
  {
    flag: FlagType.ClmR2NewClientOnboarding,
    optionValue: 'CLM_R2',
    configKey: 'clmR2Enabled',
    label: {
      id: 'AdminConstants.clmR2Label',
      defaultMessage: 'CLM R2',
      description:
        'Feature flag option to provision CLM R2 during client onboarding',
    },
    createOnly: true,
  },
];

export const getAdminConstants = (intl: IntlShape) => {
  const CLIENT_OPTIONS = [
    {
      value: ClientType.Onboarding,
      label: intl.formatMessage({
        id: 'AdminConstants.onboardingLabel',
        defaultMessage: 'Onboarding',
        description: 'Client type option for onboarding',
      }),
      disabled: true,
    },
    {
      value: ClientType.ChurnedClient,
      label: intl.formatMessage({
        id: 'AdminConstants.churnedClientLabel',
        defaultMessage: 'Churned Client',
        description: 'Client type option for churned client',
      }),
    },
    {
      value: ClientType.Internal,
      label: intl.formatMessage({
        id: 'AdminConstants.internalLabel',
        defaultMessage: 'Internal',
        description: 'Client type option for internal',
      }),
    },
    {
      value: ClientType.LiveClient,
      label: intl.formatMessage({
        id: 'AdminConstants.liveClientLabel',
        defaultMessage: 'Live Client',
        description: 'Client type option for live client',
      }),
    },
    {
      value: ClientType.Partner,
      label: intl.formatMessage({
        id: 'AdminConstants.partnerLabel',
        defaultMessage: 'Partner',
        description: 'Client type option for partner',
      }),
    },
    {
      value: ClientType.Poc,
      label: intl.formatMessage({
        id: 'AdminConstants.pocLabel',
        defaultMessage: 'Proof of Concept',
        description: 'Client type option for proof of concept',
      }),
    },
    {
      value: ClientType.QA,
      label: intl.formatMessage({
        id: 'AdminConstants.qaLabel',
        defaultMessage: 'QA',
        description: 'Client type option for QA',
      }),
      disabled: true,
    },
    {
      value: ClientType.Normal,
      label: intl.formatMessage({
        id: 'AdminConstants.normalClientLabel',
        defaultMessage: 'Normal Client',
        description: 'Client type option for normal client',
      }),
      disabled: true,
    },
    {
      value: ClientType.Pilot,
      label: intl.formatMessage({
        id: 'AdminConstants.pilotLabel',
        defaultMessage: 'Pilot',
        description: 'Client type option for pilot',
      }),
      disabled: true,
    },
  ];

  const FEATURE_FLAG_OPTIONS = [
    {
      value: FeatureFlagType.AllowClauseRenaming,
      label: intl.formatMessage({
        id: 'AdminConstants.allowClauseRenamingLabel',
        defaultMessage:
          '(ENGINEERING USE ONLY) Intelligence: Allow Clause Renaming',
        description: 'Feature flag option for allowing clause renaming',
      }),
    },
    {
      value: FeatureFlagType.EsWeightedSearch,
      label: intl.formatMessage({
        id: 'AdminConstants.esWeightedSearchLabel',
        defaultMessage: '(ENGINEERING USE ONLY) Intelligence: Weighted Search',
        description: 'Feature flag option for weighted search',
      }),
    },
    {
      value: FeatureFlagType.SingleSignOn,
      label: intl.formatMessage({
        id: 'AdminConstants.singleSignOnLabel',
        defaultMessage: 'Single Sign-On',
        description: 'Feature flag option for single sign-on',
      }),
    },
    {
      value: FeatureFlagType.Workflow,
      label: intl.formatMessage({
        id: 'AdminConstants.workflowLabel',
        defaultMessage: 'Workflow',
        description: 'Feature flag option for workflow',
      }),
    },
    {
      value: FeatureFlagType.BulkDocumentMetadata,
      label: intl.formatMessage({
        id: 'AdminConstants.bulkMetadataUpdaterLabel',
        defaultMessage: 'Bulk Metadata Updater',
        description: 'Feature flag option for bulk metadata updater',
      }),
    },
    {
      value: FeatureFlagType.SyncPairWebhook,
      label: intl.formatMessage({
        id: 'AdminConstants.evisyncWebhookLabel',
        defaultMessage: 'Evisync Webhook',
        description: 'Feature flag option for evisync webhook',
      }),
    },
  ];

  const bulkDocumentMetadataMultipleTasksValue = {
    value: FeatureFlagType.BulkDocumentMetadataMultipleTasks as const,
    label: intl.formatMessage({
      id: 'AdminConstants.bulkMetadataFastProcessingLabel',
      defaultMessage: 'Bulk Metadata Updater: Fast Processing',
      description: 'Feature flag option for bulk metadata fast processing',
    }),
  };

  const bulkDocumentMetadataFileMove = {
    value: FeatureFlagType.BulkDocumentMetadataFileMove,
    label: intl.formatMessage({
      id: 'AdminConstants.bulkMetadataMoveFilesLabel',
      defaultMessage: 'Bulk Metadata Updater: Move Files',
      description: 'Feature flag option for bulk metadata move files',
    }),
  };

  const bulkDocumentMetadataFileRename = {
    value: FeatureFlagType.BulkDocumentMetadataFilenameUpdate,
    label: intl.formatMessage({
      id: 'AdminConstants.bulkMetadataRenameFilesLabel',
      defaultMessage: 'Bulk Metadata Updater: Rename Files',
      description: 'Feature flag option for bulk metadata rename files',
    }),
  };

  return {
    CLIENT_OPTIONS,
    FEATURE_FLAG_OPTIONS,
    bulkDocumentMetadataMultipleTasksValue,
    bulkDocumentMetadataFileMove,
    bulkDocumentMetadataFileRename,
  };
};

const DEFAULT_FEATURE_FLAGS = {
  dashboardEnabled: true,
  hideExpirationsEnabled: true,
  syncPairWebhookEnabled: true,
};

export const INITIAL_CONFIGS = {
  name: '',
  subDomain: '',
  descrip: '',
  bucket: '',
  clientType: ClientType.Internal as const,
  syncType: ClientSyncType.Evifs as const,
  customAlgorithms: [],
  clauseCountLimit: 5,
  ssoEnabled: false,
  workflowEnabled: false,
  urlBuilderEnabled: false,
  xrayPromptModelQuota: 5,
  storageProvider: CloudType.Aws as const,
  tenantId: null,
  tenantEnv: null,
  suvId: null,
  cpsHost: null,
  wssHost: null,
  galahadHost: null,
  homepageHost: null,
  awsAccountId: null,
  customerBillingId: null,
  featureFlagFields: [],
  userManagement: UserManagement.Evisort,
  authenticationProvider: AuthenticationProvider.Evisort,
  ...DEFAULT_FEATURE_FLAGS,
};
